import { useMemo } from "react"
import { useSelector } from "react-redux"
import type { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"

import type {
  AlertSettingType,
  AlertSettingUpsertForm,
  DeviceAlertSetting,
} from "types/alerts.types"
import { formatMutationParams } from "helpers/utils/alerts"
import { selectCurrentOrgId } from "features/store/orgSlice"
import { useUpdateDeviceAlertSettingMutation } from "features/api"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import AlertSettingForm from "../AlertSettingForm"

interface UpdateDeviceAlertSettingProps {
  selectedAlert: DeviceAlertSetting
  onClose: () => void
  types: AlertSettingType[]
  methods: UseFormReturn<AlertSettingUpsertForm, any>
}

function UpdateDeviceAlertSetting({
  selectedAlert,
  onClose,
  types,
  methods,
}: UpdateDeviceAlertSettingProps) {
  const { t, i18n } = useTranslation()
  const orgId = useSelector(selectCurrentOrgId)
  const [updateDeviceAlertSetting, { isLoading }] = useUpdateDeviceAlertSettingMutation()

  const selectedTypeName = selectedAlert.type
  const selectedType = useMemo(
    () => types?.find((type) => type.name === selectedTypeName),
    [types, selectedTypeName],
  )

  const onSubmit = async (data: AlertSettingUpsertForm) => {
    const locale = i18n.resolvedLanguage || "en"
    const formattedData = formatMutationParams(data, selectedType, locale)
    snackbarMutation({
      mutation: updateDeviceAlertSetting({
        id: selectedAlert.id,
        org_id: Number(orgId),
        less_id: selectedAlert.less_id,
        ...formattedData,
      }).unwrap(),
      getErrorMessage: buildGetErrorMessage(
        t("error.UPDATING_ITEM", { item: t("alert_settings.ALERT_SETTING") }),
      ),
      getSuccessMessage: () =>
        t("success.UPDATING_ITEM", {
          item: t("alert_settings.ALERT_SETTING"),
          count: 1,
          context: "female",
        }),
    })
      .then(() => {
        onClose()
      })
      .catch(() => {})
  }

  return (
    <AlertSettingForm
      entity="device"
      entityId={selectedAlert.less_id}
      isLoading={isLoading}
      title="alert_settings.EDIT_ALERT_SETTING"
      onSubmit={methods.handleSubmit(onSubmit)}
      methods={methods}
      types={types}
      selectedTypeName={selectedTypeName}
      onClose={onClose}
      buttonText="generic.UPDATE"
      disableType
    />
  )
}

export default UpdateDeviceAlertSetting
